/**
 * Main-thread side of the simulator, the LiveDataManager rules without the
 * DOM: runs the generator in a module worker (or on an interval when workers
 * are unavailable), turns each batch into Reading objects (source 'sim'),
 * steps every item's timestamp in "data time" by the median interval of its
 * shipped history (default 30 days, as data-loader.js computed it), raises
 * an alert when an item's status changes to yellow or red, re-derives
 * component levels through the injected deriveStatus hook, and reports each
 * completed cycle (the old livedata:cycleComplete) so failure scenarios can
 * advance. Keys that hold a live reading are skipped here as well as in the
 * generator.
 */
import type { DataItem, Element, Reading, StatusColour, StatusLevel, Subsystem } from '../types';
import type { TwinAlert } from '../store';
import { walkSubsystems } from '../store';
import type { DeriveStatusHook } from './deriveStatusHook';
import {
  DEFAULT_VARIATION_PERCENT,
  generateBatch,
  parseValue,
  simKey,
  type FailureEffectsMap,
  type GeneratorBatch,
  type GeneratorElement,
  type WorkerInbound,
  type WorkerOutbound,
} from './generator';

export const DEFAULT_HISTORY_INTERVAL_MS = 30 * 24 * 60 * 60 * 1000;

/** The subset of Worker the simulation uses, so tests can substitute a fake. */
export interface WorkerLike {
  postMessage(message: WorkerInbound): void;
  terminate(): void;
  onmessage: ((e: { data: WorkerOutbound }) => void) | null;
  onerror: ((e: { message?: string }) => void) | null;
}

export type SimAlert = Omit<TwinAlert, 'id' | 'acknowledged' | 'timeMs'>;

export interface SimulationOptions {
  element: Element;
  intervalMs: number;
  variationPercent?: number;
  failureEffects?: FailureEffectsMap;
  /** Readings already in the store: live keys are protected, statuses seed the change detection. */
  initialReadings?: Record<string, Reading>;
  deriveStatus?: DeriveStatusHook | null;
  onReadings: (readings: Reading[]) => void;
  onAlert: (alert: SimAlert) => void;
  onCycleComplete?: (info: { timestamp: number; updates: number }) => void;
  onLevelChange?: (change: { subsystemId: string; level: StatusLevel; previous: StatusLevel }) => void;
}

export interface SimulationDeps {
  /** Returns a worker, or null to run the generator on the main thread. */
  createWorker?: () => WorkerLike | null;
  now?: () => number;
  random?: () => number;
}

export interface Simulation {
  start(): void;
  stop(): void;
  isRunning(): boolean;
  setIntervalMs(ms: number): void;
  setFailureEffects(effects: FailureEffectsMap): void;
  setExcludedKeys(keys: Iterable<string>): void;
  /** Applies one generator batch (the worker callback; public for tests). */
  applyBatch(batch: GeneratorBatch): void;
  /** Median spacing of the item's shipped history, the "data time" step. */
  historyIntervalMs(subsystemId: string, dataId: string): number | undefined;
}

interface ItemEntry {
  subsystem: Subsystem;
  item: DataItem;
  intervalMs: number;
  /** Data time of the newest point, advanced by intervalMs per update. */
  lastTimeMs: number | null;
  previousStatus: StatusColour | null;
}

/** ms for the shipped datetime forms ("2025-01-15T10:30:00Z" or "2025/01/15 10:30"); null when unparseable. */
export function parseHistoryTime(datetime: string | number | undefined): number | null {
  if (datetime === undefined || datetime === null) return null;
  if (typeof datetime === 'number') return Number.isFinite(datetime) ? datetime : null;
  let ms = Date.parse(datetime);
  if (Number.isNaN(ms)) ms = Date.parse(datetime.replace(/\//g, '-').replace(' ', 'T'));
  return Number.isNaN(ms) ? null : ms;
}

/** Median gap between the shipped history points (data-loader.js), default 30 days. */
export function historyIntervalFor(item: Pick<DataItem, 'history'>): number {
  const times = (item.history ?? []).map((h) => parseHistoryTime(h.datetime)).filter((t): t is number => t !== null);
  if (times.length < 2) return DEFAULT_HISTORY_INTERVAL_MS;
  times.sort((a, b) => b - a);
  const gaps: number[] = [];
  for (let i = 0; i < times.length - 1; i++) {
    const gap = times[i] - times[i + 1];
    if (gap > 0) gaps.push(gap);
  }
  if (gaps.length === 0) return DEFAULT_HISTORY_INTERVAL_MS;
  gaps.sort((a, b) => a - b);
  return gaps[Math.floor(gaps.length / 2)];
}

/** Newest history point in ms, or null without history. */
export function newestHistoryTime(item: Pick<DataItem, 'history'>): number | null {
  let newest: number | null = null;
  for (const h of item.history ?? []) {
    const t = parseHistoryTime(h.datetime);
    if (t !== null && (newest === null || t > newest)) newest = t;
  }
  return newest;
}

function asColour(status: unknown): StatusColour | null {
  return status === 'green' || status === 'yellow' || status === 'red' ? status : null;
}

/** Keys whose current reading is live; the generator must leave them alone. */
export function liveKeysOf(readings: Record<string, Reading>): string[] {
  return Object.keys(readings)
    .filter((k) => readings[k]?.source === 'live')
    .sort();
}

/** Default worker factory: the Vite module worker, or null when the environment has no Worker. */
export function createGeneratorWorker(): WorkerLike | null {
  if (typeof Worker === 'undefined') return null;
  return new Worker(new URL('./dataGenerator.worker.ts', import.meta.url), { type: 'module' }) as unknown as WorkerLike;
}

export function createSimulation(options: SimulationOptions, deps: SimulationDeps = {}): Simulation {
  const now = deps.now ?? (() => Date.now());
  const random = deps.random ?? Math.random;
  const createWorker = deps.createWorker ?? createGeneratorWorker;
  const variationPercent = options.variationPercent ?? DEFAULT_VARIATION_PERCENT;

  let intervalMs = options.intervalMs;
  let failureEffects: FailureEffectsMap = { ...(options.failureEffects ?? {}) };
  let excludedKeys = new Set<string>();
  const readings: Record<string, Reading> = { ...(options.initialReadings ?? {}) };
  const levels = new Map<string, StatusLevel>();
  let running = false;
  let worker: WorkerLike | null = null;
  let fallbackTimer: ReturnType<typeof setInterval> | null = null;
  let fallbackElement: GeneratorElement | null = null;

  // Index every data item: data-time step, newest point, previous status.
  const index = new Map<string, ItemEntry>();
  walkSubsystems(options.element.subsystems ?? [], (subsystem) => {
    for (const item of subsystem.data ?? []) {
      const key = simKey(subsystem.id, item.id);
      const existing = readings[key];
      index.set(key, {
        subsystem,
        item,
        intervalMs: historyIntervalFor(item),
        lastTimeMs: existing ? existing.timeMs : newestHistoryTime(item),
        previousStatus: existing ? existing.status : asColour(item.status),
      });
    }
  });

  for (const key of liveKeysOf(readings)) excludedKeys.add(key);

  // Baseline the derived levels so pre-existing conditions do not alert on start.
  function baselineLevels(): void {
    if (!options.deriveStatus) return;
    const seen = new Set<string>();
    for (const entry of index.values()) {
      if (seen.has(entry.subsystem.id)) continue;
      seen.add(entry.subsystem.id);
      levels.set(entry.subsystem.id, options.deriveStatus(entry.subsystem.id, readings));
    }
  }

  function startPayload(): WorkerInbound {
    return {
      type: 'start',
      payload: {
        element: options.element as unknown as GeneratorElement,
        interval: intervalMs,
        variationPercent,
        failureEffects,
        excludedKeys: [...excludedKeys],
      },
    };
  }

  function stopFallback(): void {
    if (fallbackTimer !== null) clearInterval(fallbackTimer);
    fallbackTimer = null;
  }

  function fallbackTick(): void {
    if (!running || !fallbackElement) return;
    const batch = generateBatch(fallbackElement, { variationPercent, failureEffects, excludedKeys, random, now });
    if (batch.updates.length > 0) applyBatch(batch);
  }

  function startFallback(): void {
    stopFallback();
    if (!fallbackElement) fallbackElement = structuredClone(options.element) as unknown as GeneratorElement;
    fallbackTimer = setInterval(fallbackTick, intervalMs);
    fallbackTick();
  }

  function startWorker(): boolean {
    let created: WorkerLike | null;
    try {
      created = createWorker();
    } catch (err) {
      console.warn('[sim] worker unavailable, generating on the main thread:', err);
      return false;
    }
    if (!created) return false;
    worker = created;
    worker.onmessage = (e) => {
      if (e.data?.type === 'batch') applyBatch(e.data.payload);
    };
    worker.onerror = (e) => {
      console.error('[sim] worker failed, generating on the main thread:', e?.message ?? e);
      worker?.terminate();
      worker = null;
      if (running) startFallback();
    };
    worker.postMessage(startPayload());
    return true;
  }

  function alertForUpdate(entry: ItemEntry, value: string, status: StatusColour): SimAlert {
    const itemName = entry.item.label || entry.item.id;
    const level = status === 'red' ? 'CRITICAL' : 'WARNING';
    return {
      severity: status === 'red' ? 'critical' : 'warning',
      title: `${itemName} Alert`,
      message: `${itemName} is now ${value} (${level})`,
      subsystemId: entry.subsystem.id,
      dataId: entry.item.id,
    };
  }

  function deriveLevels(subsystemIds: Set<string>): void {
    if (!options.deriveStatus) return;
    for (const subsystemId of subsystemIds) {
      const level = options.deriveStatus(subsystemId, readings);
      const previous = levels.get(subsystemId) ?? 'OPERATIONAL';
      if (level === previous) continue;
      levels.set(subsystemId, level);
      options.onLevelChange?.({ subsystemId, level, previous });
      if (level === 'DEGRADED' || level === 'OFFLINE') {
        const subsystem = [...index.values()].find((e) => e.subsystem.id === subsystemId)?.subsystem;
        const name = subsystem?.label || subsystemId;
        options.onAlert({
          severity: level === 'OFFLINE' ? 'critical' : 'warning',
          title: `${name} Status Change`,
          message: `${name} status changed to ${level}`,
          subsystemId,
        });
      }
    }
  }

  function applyBatch(batch: GeneratorBatch): void {
    const produced: Reading[] = [];
    const touched = new Set<string>();
    const t = now();
    for (const update of batch.updates) {
      const key = simKey(update.subsystemId, update.dataId);
      const entry = index.get(key);
      if (!entry) {
        console.warn(`[sim] update for unknown item ${key} ignored`);
        continue;
      }
      // A live reading arrived for this key after the generator was told; keep it.
      if (readings[key]?.source === 'live' || excludedKeys.has(key)) continue;
      const timeMs = entry.lastTimeMs === null ? t : entry.lastTimeMs + entry.intervalMs;
      entry.lastTimeMs = timeMs;
      const reading: Reading = {
        subsystemId: update.subsystemId,
        dataId: update.dataId,
        value: update.newValue,
        numericValue: update.numericValue,
        unit: parseValue(update.newValue)?.unit || undefined,
        status: update.status,
        timeMs,
        source: 'sim',
        freshnessMs: 0,
      };
      if (update.status !== entry.previousStatus && (update.status === 'yellow' || update.status === 'red')) {
        options.onAlert(alertForUpdate(entry, update.newValue, update.status));
      }
      entry.previousStatus = update.status;
      readings[key] = reading;
      produced.push(reading);
      touched.add(update.subsystemId);
    }
    if (produced.length > 0) options.onReadings(produced);
    deriveLevels(touched);
    options.onCycleComplete?.({ timestamp: t, updates: produced.length });
  }

  return {
    start() {
      if (running) return;
      running = true;
      baselineLevels();
      if (!startWorker()) startFallback();
    },
    stop() {
      running = false;
      stopFallback();
      if (worker) {
        worker.postMessage({ type: 'stop' });
        worker.terminate();
        worker = null;
      }
    },
    isRunning: () => running,
    setIntervalMs(ms) {
      intervalMs = ms;
      if (!running) return;
      if (worker) worker.postMessage({ type: 'setInterval', payload: ms });
      else startFallback();
    },
    setFailureEffects(effects) {
      failureEffects = { ...effects };
      if (running && worker) worker.postMessage({ type: 'updateFailureEffects', payload: failureEffects });
    },
    setExcludedKeys(keys) {
      excludedKeys = new Set(keys);
      if (running && worker) worker.postMessage({ type: 'setExcludedKeys', payload: [...excludedKeys] });
    },
    applyBatch,
    historyIntervalMs: (subsystemId, dataId) => index.get(simKey(subsystemId, dataId))?.intervalMs,
  };
}
